import React, { useState, useEffect } from 'react';
import ApiService from '../../../services/apiService';
import './NotificationVCSection.css';

const NotificationVCSection = () => {
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // VC Information
  const vcInfo = {
    name: "Prof. V. Venkata Basava Rao",
    position: "Hon'ble Vice Chancellor",
    subPosition: "Rayalaseema University",
    image: "/person/VC_VB_Profile__Photo.jpg",
    videoId: "_3OcC4JwJGY?start=9424" // Video starts at 2:37:04 (9424 seconds)
  };

  // Dummy data for fallback when backend fails or during loading
  const dummyNotifications = [
    {
      id: 'dummy-1',
      title: 'Academic Calendar for Academic Year 2024-25',
      date: '2024-10-15',
      category: 'EXAMINATION',
      link: '#'
    },
    {
      id: 'dummy-2',
      title: 'Tender Notice for Library Renovation Project',
      date: '2024-10-10',
      category: 'TENDERS',
      link: '#'
    },
    {
      id: 'dummy-3',
      title: 'Circular: Guidelines for Student Admission Process',
      date: '2024-10-05',
      category: 'CIRCULARS',
      link: '#'
    },
    {
      id: 'dummy-4',
      title: 'Press Release: University Rankings 2024',
      date: '2024-09-28',
      category: 'PRESS NOTES',
      link: '#'
    },
    {
      id: 'dummy-5',
      title: 'Examination Schedule for Semester End Exams',
      date: '2024-09-25',
      category: 'EXAMINATION',
      link: '#'
    },
    {
      id: 'dummy-6',
      title: 'Tender for Campus Infrastructure Development',
      date: '2024-09-20',
      category: 'TENDERS',
      link: '#'
    }
  ];

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        console.log('🔄 Loading notifications from WordPress API...');
        
        const response = await ApiService.getNotifications();
        console.log('📥 API Response received:', response);
        
        if (response.data && response.data.length > 0) {
          // Transform WordPress API response to match our component structure
          const transformedData = response.data.map((item) => {
            // Determine the link - priority: document_link > file_upload.url > default
            let link = '#';
            if (item.acf?.document_link) {
              link = item.acf.document_link;
            } else if (item.acf?.file_upload?.url) {
              link = item.acf.file_upload.url;
            }
            
            return {
              id: item.id,
              title: item.title.rendered,
              date: item.acf?.date || item.date, // Use ACF date if available, fallback to post date
              category: item.acf?.category?.value || item.acf?.category?.label || 'GENERAL',
              link: link
            };
          });
          
          console.log('✅ Using WordPress API notifications data:', transformedData);
          console.log('📊 Number of notifications from API:', transformedData.length);
          
          setNotifications(transformedData);
          setError(null);
        } else {
          console.log('⚠️  No notifications from API');
          setNotifications([]);
          setError('No notifications available from API');
        }
      } catch (err) {
        console.error('❌ Error loading notifications from WordPress API:', err);
        setNotifications([]);
        setError('Failed to load notifications from API');
      } finally {
        setLoading(false);
        console.log('🏁 Notifications loading completed');
      }
    };

    fetchNotifications();
  }, []);

  const categories = ['ALL', 'Examination', 'Tenders', 'Circulars', 'Press Notes', 'General'];

  // Use only API notifications data
  const displayNotifications = notifications;
  
  const filteredNotifications = displayNotifications.filter(notification => {
    if (activeFilter === 'ALL') return true;
    return notification.category === activeFilter;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'EXAMINATION': '#e74c3c',
      'TENDERS': '#3498db',
      'CIRCULARS': '#f39c12',
      'PRESS NOTES': '#27ae60',
      'Press Notes': '#27ae60',
      'GENERAL': '#9b59b6',
      'ALL': '#95a5a6'
    };
    return colors[category] || '#95a5a6';
  };

  return (
    <section id="notification-vc-section-main" className="notification-vc-section">
      <div id="notification-vc-container" className="notification-vc-container">
        
        {/* Left Side - Notifications */}
        <div id="notifications-side" className="notifications-side">
          <div id="notifications-header" className="notifications-header">
            <h2 id="notifications-title">Notifications</h2>
            
            {/* Category Filters */}
            <div id="category-filters" className="category-filters">
              {categories.map((category) => (
                <button
                  key={category}
                  id={`category-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`category-filter-btn ${activeFilter === category ? 'active' : ''}`}
                  onClick={() => setActiveFilter(category)}
                  style={{ backgroundColor: activeFilter === category ? getCategoryColor(category) : 'transparent' }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div id="notifications-content" className="notifications-content">
            {loading ? (
              <div id="notifications-loading" className="loading-state">
                <div id="loading-spinner" className="loading-spinner"></div>
              </div>
            ) : (
              <div id="notifications-scroll-container" className="notifications-scroll-container">
                <div id="notifications-list" className="notifications-list" style={{ height: '240px', overflowY: 'auto' }}>
                  {filteredNotifications.length > 0 ? (
                    // Duplicate notifications for seamless scrolling animation
                    [...filteredNotifications, ...filteredNotifications].map((notification, index) => (
                      <div key={`${notification.id}-${index}`} style={{ borderBottom: '1px solid #ddd' }}>
                        <a
                          href={notification.link && notification.link !== '#' ? notification.link : undefined}
                          target={notification.link && notification.link !== '#' ? '_blank' : undefined}
                          rel={notification.link && notification.link !== '#' ? 'noopener noreferrer' : undefined}
                          onClick={(e) => {
                            if (!notification.link || notification.link === '#') {
                              e.preventDefault();
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobile ? '8px' : '15px',
                            padding: isMobile ? '8px 0' : '12px 0',
                            textDecoration: 'none',
                            color: 'inherit',
                            cursor: (notification.link && notification.link !== '#') ? 'pointer' : 'default',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (notification.link && notification.link !== '#') {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span 
                            style={{
                              fontSize: isMobile ? '9px' : '11px',
                              color: '#666',
                              minWidth: isMobile ? '55px' : '70px',
                              fontWeight: '500',
                              flexShrink: 0
                            }}
                          >
                            {formatDate(notification.date)}
                          </span>
                          
                          <span 
                            style={{ 
                              backgroundColor: getCategoryColor(notification.category),
                              color: 'white',
                              padding: isMobile ? '1px 3px' : '2px 6px',
                              borderRadius: isMobile ? '6px' : '10px',
                              fontSize: isMobile ? '7px' : '9px',
                              fontWeight: 'bold',
                              minWidth: 'fit-content',
                              textAlign: 'center',
                              textTransform: 'uppercase',
                              flexShrink: 0,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {notification.category}
                          </span>
                          
                          <span 
                            style={{
                              flex: 1,
                              fontSize: isMobile ? '11px' : '15px',
                              color: '#333',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineHeight: '1.3',
                              minWidth: 0
                            }}
                          >
                            {notification.title}
                          </span>
                        </a>
                      </div>
                    ))
                  ) : (
                    <div id="no-notifications" className="no-notifications">
                      <p>No notifications available</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - VC's Message */}
        <div id="vc-message-side" className="vc-message-side">
          <div id="vc-message-header" className="vc-message-header">
            <h2 id="vc-message-title">Vice Chancellor's Message</h2>
            <p id="vc-message-subtitle">A message of inspiration and guidance</p>
          </div>

          <div id="vc-message-content" className="vc-message-content">
            <div id="vc-video-container" className="vc-video-container">
              <div id="vc-youtube-wrapper" className="vc-youtube-wrapper">
                <iframe
                  id="vc-message-video-iframe"
                  src={`//www.youtube.com/embed/${vcInfo.videoId}`}
                  title="Vice Chancellor's Message"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </section>
  );
};

export default NotificationVCSection;